import { useMemo, useCallback } from 'react';
import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';

import { BANNER_NAV_CONFIG } from '../banner-navigation.config';
import { useAuthToken } from '@/store/auth';
import { useSiteStore } from '@/store/site';
import { useViewMode } from '@/store/appearance';
import { sportService } from '@/services/sport.service';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useGetSiteConfigsWithSession } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { useMenuTypeList, useOriginMenuData } from '@/hooks/query';
import { setKafkaNormal, useKafkaLog } from '@/hooks/query/use-kafka-log';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';
import { useRewardSystemTrigger } from '../../options';

import type { CheckIsEnabledParameters, NavigationContext } from '../types';

export const useBannerNavigation = () => {
	const router = useRouter();
	const locale = useLocale();

	const { isMars } = useViewMode();
	const [setCurrentPage] = useSiteStore(state => [state.setCurrentPage]);

	const authToken = useAuthToken();
	const isBefore = !authToken;
	const { user } = useAccountInfo();

	const { SiteConfigsData } = useGetSiteConfigs();
	const { SiteConfigsWithSessionData } = useGetSiteConfigsWithSession();
	const { enableESoccerBattlePromotion } = useLicSettings();

	const { menuResponse } = useOriginMenuData({ isNeedParlayData: false });
	const { menuTypeList } = useMenuTypeList();

	const { openPredictionMarket } = usePredictionMarket();
	const { triggerWithCare } = useRewardSystemTrigger();
	const kafkaLog = useKafkaLog();
	
	const hasMenuType51 = menuTypeList.some(item => item.modeId == 51);

	const isEnabledContext = useMemo<Partial<NavigationContext>>(() => ({
		enableESoccerBattlePromotion,
		hasMenuType51,
		SiteConfigsData,
		SiteConfigsWithSessionData,
		user,
	}), [
		enableESoccerBattlePromotion,
		hasMenuType51,
		SiteConfigsData,
		SiteConfigsWithSessionData,
		user,
	]);

	const checkIsEnabled = useCallback(
		(key: string, params: CheckIsEnabledParameters) => {
			const config = BANNER_NAV_CONFIG[key];

			if (!config) return true;
			if (!config?.checkIsEnabled) return false;
			return config.checkIsEnabled(params, isEnabledContext as NavigationContext);
		},
		[isEnabledContext]
	);

	const navigate = useCallback(
		(key: string) => {
			const config = BANNER_NAV_CONFIG[key];

			if (!config || !config.navigate) {
				return;
			}

			const context: NavigationContext = {
				router,
				locale,
				user,
				authToken,
				isBefore,
				menuResponse,
				hasMenuType51,
				isMars,
				enableESoccerBattlePromotion,
				SiteConfigsData,
				SiteConfigsWithSessionData,
				openPredictionMarket,
				triggerWithCare,
				kafkaLog,
				setKafkaNormal,
				navigateToSports: (path: string) => {
					const sportIdMatch = path.match(/\/sports\/(\d+)/);
					const sportId = sportIdMatch?.[1] ? Number(sportIdMatch[1]) : undefined;
					if (sportId && sportService.isAnyVirtualSportsProduct(sportId)) {
						setCurrentPage('lbl_VirtualSport');
					}
					router.push(path, { locale });
				},
			};
			
			if (config.checkIsUnderMaintain) {
				const umCheckResult = config.checkIsUnderMaintain();

				if (umCheckResult.isUnderMaintain) {
					config.promptForNavigationStopped?.();
					return;
				}

				if (umCheckResult.redirectUrl) {
					const contextWithRedirect = { 
						...context, 
						redirectUrl: umCheckResult.redirectUrl 
					};
					config.navigate(contextWithRedirect);
					return;
				}
			}

			config.navigate(context);
		},
		[
			router,
			locale,
			user,
			authToken,
			isBefore,
			menuResponse,
			hasMenuType51,
			isMars,
			enableESoccerBattlePromotion,
			SiteConfigsData,
			SiteConfigsWithSessionData,
			openPredictionMarket,
			triggerWithCare,
			kafkaLog,
			setCurrentPage,
		]
	);

	return {
		checkIsEnabled,
		navigate
	};
};